import React from 'react';
import { ChevronDown } from 'lucide-react';

// Utility to merge classNames with clsx and tailwind-merge logic
export function cn(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

// Props for shared input components
export interface BaseInputProps {
  label: string;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  className?: string;
}

// NumberInput Component
export interface NumberInputProps extends Omit<BaseInputProps, 'value' | 'onChange'> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

export const NumberInput: React.FC<NumberInputProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 0.1,
  disabled = false,
  className = '',
}) => (
  <div className={cn('flex flex-col gap-1', className)}>
    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
      {label}
    </label>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50"
    />
  </div>
);

// SliderInput Component
export interface SliderInputProps extends Omit<BaseInputProps, 'value' | 'onChange'> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  showInput?: boolean;
}

export const SliderInput: React.FC<SliderInputProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  showInput = true,
  disabled = false,
  className = '',
}) => (
  <div className={cn('flex flex-col gap-1', className)}>
    <div className="flex justify-between">
      <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      {showInput && (
        <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
          {value.toFixed(1)}
        </span>
      )}
    </div>
    <input
      type="range"
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      className="w-full h-2 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
    />
  </div>
);

// SelectInput Component
export interface SelectInputProps extends Omit<BaseInputProps, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}

export const SelectInput: React.FC<SelectInputProps> = ({
  label,
  value,
  onChange,
  options,
  disabled = false,
  className = '',
}) => (
  <div className={cn('flex flex-col gap-1', className)}>
    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

// ToggleInput Component
export interface ToggleInputProps extends Omit<BaseInputProps, 'value' | 'onChange'> {
  value: boolean;
  onChange: (value: boolean) => void;
}

export const ToggleInput: React.FC<ToggleInputProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  className = '',
}) => (
  <div className={cn('flex items-center justify-between', className)}>
    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
      {label}
    </label>
    <button
      onClick={() => onChange(!value)}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
        value
          ? 'bg-blue-500'
          : 'bg-gray-300 dark:bg-gray-600',
        disabled && 'opacity-50'
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
          value ? 'translate-x-4' : 'translate-x-0.5'
        )}
      />
    </button>
  </div>
);

// PanelSection Component
export interface PanelSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
}

export const PanelSection: React.FC<PanelSectionProps> = ({
  title,
  children,
  defaultOpen = true,
  className = '',
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <div className={cn('border-b border-gray-200 dark:border-gray-700', className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h3>
        <ChevronDown
          size={16}
          className={cn(
            'text-gray-600 dark:text-gray-400 transition-transform',
            !isOpen && '-rotate-90'
          )}
        />
      </button>
      {isOpen && (
        <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

// IconButton Component
export interface IconButtonProps {
  icon: React.ReactNode;
  tooltip?: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  tooltip,
  onClick,
  active = false,
  disabled = false,
  className = '',
  size = 'md',
}) => {
  const sizeClasses = {
    sm: 'p-1.5 text-base',
    md: 'p-2 text-lg',
    lg: 'p-2.5 text-xl',
  };

  const baseClass = cn(
    'inline-flex items-center justify-center rounded transition-colors',
    sizeClasses[size],
    active
      ? 'bg-blue-500 text-white'
      : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600',
    disabled && 'opacity-50 cursor-not-allowed',
    className
  );

  const button = (
    <button
      onClick={onClick}
      disabled={disabled}
      className={baseClass}
      title={tooltip}
    >
      {icon}
    </button>
  );

  if (!tooltip) return button;

  return (
    <div className="group relative inline-flex">
      {button}
      <div className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded whitespace-nowrap z-50">
        {tooltip}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-100" />
      </div>
    </div>
  );
};

// TextInput Component
export interface TextInputProps extends Omit<BaseInputProps, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
}

export const TextInput: React.FC<TextInputProps> = ({
  label,
  value,
  onChange,
  placeholder = '',
  multiline = false,
  rows = 3,
  disabled = false,
  className = '',
}) => (
  <div className={cn('flex flex-col gap-1', className)}>
    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
      {label}
    </label>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50 resize-none font-mono text-xs"
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50"
      />
    )}
  </div>
);

// ColorInput Component
export interface ColorInputProps extends Omit<BaseInputProps, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
}

export const ColorInput: React.FC<ColorInputProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  className = '',
}) => (
  <div className={cn('flex flex-col gap-1', className)}>
    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
      {label}
    </label>
    <div className="flex gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="h-8 w-12 border border-gray-300 dark:border-gray-600 rounded cursor-pointer disabled:opacity-50"
      />
      <span className="text-xs font-mono text-gray-500 dark:text-gray-400 flex items-center">
        {value}
      </span>
    </div>
  </div>
);
